(function () {
    'use strict';

    if (window.__priorityProductLoaderInitialized) return;
    window.__priorityProductLoaderInitialized = true;

    class PriorityProductLoader {
        constructor() {
            this.priorityProductIds = new Set();
            this.isProcessing = false;
            this.grid = null;
            this.gridContainer = null;
            this.sectionId = null;
            this.observer = null;
            this._reprocessTimer = null;

            this.init();
        }

        init() {
            this.gridContainer = document.getElementById('CollectionProductGrid');
            if (!this.gridContainer) return;

            this.grid = this.gridContainer.querySelector('.productListing');
            if (!this.grid) return;

            const gridEl = document.getElementById('main-collection-product-grid');
            this.sectionId = gridEl?.dataset?.id
                || JSON.parse(sessionStorage.getItem('productGridId'));

            if (!this.sectionId) { this.reveal(); return; }
            if (this.isAlreadyFilteredByLaunchGroup()) { this.reveal(); return; }

            this.processGrid();
            this.observeGridChanges();
            this.patchInfiniteScroll();
        }

        reveal() {
            if (this.grid) this.grid.classList.add('priority-ready');
        }

        isAlreadyFilteredByLaunchGroup() {
            const params = new URLSearchParams(window.location.search);
            for (const [key, value] of params.entries()) {
                if (key.includes('launch_group') && value) return true;
            }
            return false;
        }

        async processGrid() {
            if (this.isProcessing) return;
            this.isProcessing = true;

            this.grid = this.gridContainer.querySelector('.productListing');
            if (!this.grid) { this.isProcessing = false; return; }
            if (this.isAlreadyFilteredByLaunchGroup()) { this.isProcessing = false; this.reveal(); return; }

            if (this.observer) this.observer.disconnect();

            try {
                // Fast path: session cache covers current DOM — sort locally, no network
                if (this.trySortFromCache()) {
                    this.runPostRenderHooks();
                    return;
                }

                const currentParams = this.getCurrentFilterParams();

                // Fetch LG1 and LG2 products from the metafield filter (source of truth)
                const [lg1Fetched, lg2Fetched] = await Promise.all([
                    this.fetchProducts('Launch Group 1', currentParams),
                    this.fetchProducts('Launch Group 2', currentParams),
                ]);

                // Build ID sets
                const lg1Ids = new Set(lg1Fetched.map(li => this.getProductId(li)).filter(Boolean));
                const lg2Ids = new Set(lg2Fetched.map(li => this.getProductId(li)).filter(Boolean));

                // Remove any overlap (if a product is in both, it's LG1)
                lg1Ids.forEach(id => lg2Ids.delete(id));

                this.priorityProductIds = new Set([...lg1Ids, ...lg2Ids]);

                // Get all items currently in the grid
                const currentItems = Array.from(this.grid.querySelectorAll(':scope > li.product'));
                const currentIds = new Set(currentItems.map(li => this.getProductId(li)).filter(Boolean));

                // Sort current items into buckets using metafield IDs
                const lg1Existing = [];
                const lg2Existing = [];
                const rest = [];

                currentItems.forEach(li => {
                    const id = this.getProductId(li);
                    if (id && lg1Ids.has(id)) lg1Existing.push(li);
                    else if (id && lg2Ids.has(id)) lg2Existing.push(li);
                    else rest.push(li);
                });

                // Add fetched products that aren't already on the page
                const newLg1 = lg1Fetched.filter(li => !currentIds.has(this.getProductId(li)));
                const newLg2 = lg2Fetched.filter(li => {
                    const id = this.getProductId(li);
                    return !currentIds.has(id) && !lg1Ids.has(id);
                });

                newLg1.forEach(li => lg1Existing.push(document.adoptNode(li)));
                newLg2.forEach(li => lg2Existing.push(document.adoptNode(li)));

                // Tag items for debug visibility
                lg1Existing.forEach(li => li.setAttribute('data-launch-group', '1'));
                lg2Existing.forEach(li => li.setAttribute('data-launch-group', '2'));

                // Clear and rebuild: LG1 first, then LG2, then everything else
                currentItems.forEach(li => li.remove());
                lg1Existing.forEach(li => this.grid.appendChild(li));
                lg2Existing.forEach(li => this.grid.appendChild(li));
                rest.forEach(li => this.grid.appendChild(li));

                // Persist sort for this session so repeat visits skip the fetch
                this.writeCache([...lg1Ids], [...lg2Ids]);

                this.runPostRenderHooks();
            } catch (err) {
                console.error('[PriorityProductLoader] Error:', err);
            } finally {
                this.isProcessing = false;
                this.reveal();
                this.reconnectObserver();
            }
        }

        trySortFromCache() {
            const cache = window.__priorityCache;
            if (!cache || !Array.isArray(cache.lg1Ids) || !Array.isArray(cache.lg2Ids)) return false;

            const lg1Ids = new Set(cache.lg1Ids);
            const lg2Ids = new Set(cache.lg2Ids);
            lg1Ids.forEach(id => lg2Ids.delete(id));

            const currentItems = Array.from(this.grid.querySelectorAll(':scope > li.product'));
            const currentIds = new Set(currentItems.map(li => this.getProductId(li)).filter(Boolean));

            // Only safe to skip fetch if every cached priority product is already rendered
            for (const id of lg1Ids) if (!currentIds.has(id)) return false;
            for (const id of lg2Ids) if (!currentIds.has(id)) return false;

            this.priorityProductIds = new Set([...lg1Ids, ...lg2Ids]);

            const lg1Existing = [], lg2Existing = [], rest = [];
            currentItems.forEach(li => {
                const id = this.getProductId(li);
                if (id && lg1Ids.has(id)) lg1Existing.push(li);
                else if (id && lg2Ids.has(id)) lg2Existing.push(li);
                else rest.push(li);
            });

            lg1Existing.forEach(li => li.setAttribute('data-launch-group', '1'));
            lg2Existing.forEach(li => li.setAttribute('data-launch-group', '2'));

            currentItems.forEach(li => li.remove());
            lg1Existing.forEach(li => this.grid.appendChild(li));
            lg2Existing.forEach(li => this.grid.appendChild(li));
            rest.forEach(li => this.grid.appendChild(li));

            return true;
        }

        writeCache(lg1Ids, lg2Ids) {
            try {
                const key = window.__priorityCacheKey;
                if (!key) return;
                sessionStorage.setItem(key, JSON.stringify({ lg1Ids, lg2Ids, ts: Date.now() }));
            } catch (e) {}
        }

        async fetchProducts(launchGroupValue, baseParams) {
            const allItems = [];
            const seenIds = new Set();
            let page = 1;
            const maxPages = 10;

            while (page <= maxPages) {
                const params = new URLSearchParams(baseParams);
                params.set('filter.p.m.custom.launch_group', launchGroupValue);
                params.set('page', page);
                params.delete('section_id');

                const url = `${window.location.pathname}?section_id=${this.sectionId}&${params.toString()}`;

                try {
                    let html = null;

                    // Reuse the in-flight prefetch for page 1 if it matches this launch group
                    if (page === 1 && window.__priorityPrefetch) {
                        const key = launchGroupValue === 'Launch Group 1' ? 'lg1'
                                  : launchGroupValue === 'Launch Group 2' ? 'lg2'
                                  : null;
                        if (key && window.__priorityPrefetch[key]) {
                            try { html = await window.__priorityPrefetch[key]; } catch { html = null; }
                            window.__priorityPrefetch[key] = null;
                        }
                    }

                    if (!html) {
                        const response = await fetch(url);
                        if (!response.ok) break;
                        html = await response.text();
                    }

                    const doc = new DOMParser().parseFromString(html, 'text/html');
                    const grid = doc.getElementById('main-collection-product-grid');
                    if (!grid) break;

                    const items = Array.from(grid.querySelectorAll(':scope > li.product'));
                    if (items.length === 0) break;

                    // Dedup within fetched results
                    items.forEach(li => {
                        const id = this.getProductId(li);
                        if (id && !seenIds.has(id)) {
                            seenIds.add(id);
                            allItems.push(li);
                        }
                    });

                    const hasNext = doc.querySelector('[data-infinite-scrolling]')
                        || doc.querySelector('.pagination .next')
                        || doc.querySelector('.pagination__item--next');
                    if (!hasNext) break;

                    page++;
                } catch {
                    break;
                }
            }

            return allItems;
        }

        getCurrentFilterParams() {
            const params = new URLSearchParams(window.location.search);
            params.delete('section_id');
            return params.toString();
        }

        getProductId(li) {
            const el = li.querySelector('.product-item[data-product-id]');
            return el ? el.getAttribute('data-product-id') : null;
        }

        observeGridChanges() {
            this.observeTarget = this.gridContainer.querySelector('.collection');
            if (!this.observeTarget) return;

            this.observer = new MutationObserver(() => {
                if (this.isProcessing) return;

                const grid = this.gridContainer.querySelector('.productListing');
                if (!grid) return;

                // If sorted items still exist, this is a load-more — just dedup
                const hasSortedItems = grid.querySelector('[data-launch-group]');
                if (hasSortedItems) {
                    grid.querySelectorAll(':scope > li.product').forEach(li => {
                        if (li.hasAttribute('data-launch-group')) return;
                        const id = this.getProductId(li);
                        if (id && this.priorityProductIds.has(id)) {
                            li.remove();
                        }
                    });
                    return;
                }

                // No sorted items = grid was fully replaced (filter change) — re-process
                clearTimeout(this._reprocessTimer);
                this._reprocessTimer = setTimeout(() => this.processGrid(), 100);
            });

            this.reconnectObserver();
        }

        reconnectObserver() {
            if (!this.observer || !this.observeTarget) return;
            this.observer.observe(this.observeTarget, { childList: true, subtree: true });
        }

        patchInfiniteScroll() {
            if (typeof halo === 'undefined' || typeof halo.ajaxInfiniteScrollingMapData !== 'function') return;
            const original = halo.ajaxInfiniteScrollingMapData.bind(halo);
            const self = this;
            halo.ajaxInfiniteScrollingMapData = function (data) {
                self.isLoadingMore = true;
                original(data);

                const grid = self.gridContainer.querySelector('.productListing');
                if (!grid) { self.isLoadingMore = false; return; }

                grid.querySelectorAll(':scope > li.product').forEach(li => {
                    if (li.hasAttribute('data-launch-group')) return;
                    const id = self.getProductId(li);
                    if (id && self.priorityProductIds.has(id)) {
                        li.remove();
                    }
                });

                // Reset flag after a tick so MutationObserver ignores this batch
                setTimeout(() => { self.isLoadingMore = false; }, 200);
            };
        }

        runPostRenderHooks() {
            try { if (window.wishlist?.show && typeof CollectionFiltersForm !== 'undefined') CollectionFiltersForm.setLocalStorageProductForWishlist(); } catch (e) {}
            try { if (window.compare?.show && typeof CollectionFiltersForm !== 'undefined') CollectionFiltersForm.setLocalStorageProductForCompare({ link: $('[data-compare-link]'), onComplete: null }); } catch (e) {}
            try { if (typeof CollectionFiltersForm !== 'undefined' && CollectionFiltersForm.checkNeedToConvertCurrency()) { var c = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency'); Currency.convertAll(window.shop_currency, c, 'span.money', 'money_format'); } } catch (e) {}
            try { $('.productListing .card .swatch-label.is-active').trigger('click'); } catch (e) {}
            try { window.sharedFunctions?.swapHoverVideoProductCard?.(); } catch (e) {}
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => new PriorityProductLoader());
    } else {
        new PriorityProductLoader();
    }
})();
